import { useState } from "react"
import { Check, ShieldCheck } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Card, Field, Input, SectionHeading } from "../../components/ui"

export function SecuritySettings() {
	const { user, refreshUser, toast } = useApp()
	const [current, setCurrent] = useState("")
	const [next, setNext] = useState("")
	const [confirm, setConfirm] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	async function save(event) {
		event.preventDefault()
		if (next !== confirm) {
			setError("The two new passwords do not match.")
			return
		}
		setBusy(true)
		setError(null)
		try {
			await api.changePassword({ current_password: current, new_password: next })
			await refreshUser()
			setCurrent("")
			setNext("")
			setConfirm("")
			toast("Password updated", "success")
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="space-y-5">
			<Card className="p-5">
				<SectionHeading title="Two-factor sign-in" className="mb-3" />
				<div className="flex items-start gap-3 rounded-lg bg-state-closed-soft px-3.5 py-3">
					<ShieldCheck
						size={17}
						className="mt-0.5 shrink-0 text-state-closed"
						aria-hidden
					/>
					<div>
						<p className="text-[0.8125rem] font-medium text-ink-900">Always on</p>
						<p className="mt-0.5 text-2xs leading-relaxed text-ink-600">
							Every sign-in with a password sends a six-digit code to{" "}
							<strong className="font-medium">{user?.email}</strong>. A leaked
							password is not enough on its own, so there is nothing here to switch
							off.
						</p>
					</div>
				</div>
				{user?.oauth_providers?.length > 0 && (
					<p className="mt-3 text-2xs leading-relaxed text-ink-500">
						You can also sign in with{" "}
						<strong className="font-medium text-ink-700">
							{user.oauth_providers.join(", ")}
						</strong>
						. Those providers verify your address themselves, so they skip the code.
					</p>
				)}
			</Card>

			<Card className="p-5">
				<SectionHeading
					title={user?.has_password ? "Change password" : "Set a password"}
					description={
						user?.has_password
							? "You will stay signed in on this device."
							: "You signed up with a provider. Setting a password adds a second way in."
					}
					className="mb-4"
				/>
				<form onSubmit={save} className="space-y-3.5">
					{user?.has_password && (
						<Field label="Current password">
							<Input
								type="password"
								autoComplete="current-password"
								value={current}
								onChange={(event) => setCurrent(event.target.value)}
								required
							/>
						</Field>
					)}
					<Field label="New password">
						<Input
							type="password"
							autoComplete="new-password"
							value={next}
							onChange={(event) => setNext(event.target.value)}
							required
						/>
					</Field>
					<Field label="Confirm new password" error={error}>
						<Input
							type="password"
							autoComplete="new-password"
							value={confirm}
							onChange={(event) => setConfirm(event.target.value)}
							required
						/>
					</Field>
					<div className="flex justify-end">
						<Button
							type="submit"
							variant="primary"
							icon={Check}
							busy={busy}
							disabled={busy || !next}
						>
							Update password
						</Button>
					</div>
				</form>
			</Card>
		</div>
	)
}
